import { Link } from 'react-router-dom';
import { LegalChrome } from './LegalChrome';
import { MAILING_ADDRESS, SUPPORT_EMAIL } from './constants';

const LAST_UPDATED = 'June 22, 2026';

export function TermsOfServicePage() {
  return (
    <LegalChrome>
      <h1 className="legal-h1">Terms of Service</h1>
      <p className="legal-muted">Last updated: {LAST_UPDATED}</p>

      <p className="legal-p">
        These Terms of Service (&quot;Terms&quot;) are a legal agreement between you and Elyxora
        Works, LLC (&quot;Elyxora,&quot; &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;)
        governing your access to and use of the Kynward mobile application, website, and related
        services (collectively, the &quot;Services&quot;). By creating an account or using the
        Services, you agree to these Terms and our{' '}
        <Link className="legal-a" to="/legal/privacy-policy">
          Privacy Policy
        </Link>
        . If you do not agree, do not use the Services.
      </p>

      <h2 className="legal-h2">1. Eligibility</h2>
      <p className="legal-p">
        The Services are intended only for adults. You must be at least 18 years old (or the age of
        legal majority where you live, if higher) to use Kynward. By using the Services, you
        represent and warrant that you meet this requirement and that all registration information
        you provide is accurate.
      </p>
      <p className="legal-p">
        You may not use the Services if you have been previously suspended or removed, if you are
        prohibited from using the Services under applicable law, or if your use would violate any
        applicable law or regulation.
      </p>

      <h2 className="legal-h2">2. The Services</h2>
      <p className="legal-p">
        Kynward is a conversation-first social product. Features may include small matched groups
        (&quot;Circles&quot;), daily shared prompts, group chat, Signal Fire, mutual connections and
        private chats, meetup proposals, subscriptions, and related safety and profile tools. We may
        add, change, or remove features at any time.
      </p>
      <p className="legal-p">
        Kynward is not a background-checking service, emergency service, or professional counseling
        service. We do not guarantee that you will meet any particular person, form any particular
        relationship, or have any particular outcome from using the Services.
      </p>

      <h2 className="legal-h2">3. Your account</h2>
      <p className="legal-p">
        You are responsible for maintaining the confidentiality of your login credentials and for
        all activity under your account. Notify us promptly at{' '}
        <a className="legal-a" href={`mailto:${SUPPORT_EMAIL}`}>
          {SUPPORT_EMAIL}
        </a>{' '}
        if you believe your account has been compromised.
      </p>
      <p className="legal-p">
        You may have only one personal account unless we authorize otherwise. You may not sell,
        transfer, or share your account with anyone else.
      </p>

      <h2 className="legal-h2">4. User content and license</h2>
      <p className="legal-p">
        You may submit or share content through the Services, including profile information, photos,
        messages, Campfire responses, Signal Fire replies, and other materials (&quot;User
        Content&quot;). You retain ownership of your User Content, subject to the license below.
      </p>
      <p className="legal-p">
        By submitting User Content, you grant Elyxora a non-exclusive, royalty-free, worldwide,
        sublicensable, transferable license to host, store, use, reproduce, modify, adapt, publish,
        display, perform, distribute, and otherwise process your User Content as reasonably
        necessary to operate, provide, secure, improve, and promote the Services, including for
        moderation, trust and safety, backups, and technical delivery.
      </p>
      <p className="legal-p">
        You represent that you have all rights necessary to grant this license and that your User
        Content does not violate these Terms, our{' '}
        <Link className="legal-a" to="/legal/community-guidelines">
          Community Guidelines
        </Link>
        , or any applicable law.
      </p>

      <h2 className="legal-h2">5. Prohibited conduct</h2>
      <p className="legal-p">You agree not to:</p>
      <ul className="legal-ul">
        <li className="legal-li">
          harass, threaten, bully, stalk, discriminate against, or abuse others
        </li>
        <li className="legal-li">
          impersonate any person or entity, or misrepresent your affiliation, age, or identity
        </li>
        <li className="legal-li">
          post or share illegal content, child sexual abuse material, non-consensual intimate
          imagery, or content that promotes violence, exploitation, or self-harm
        </li>
        <li className="legal-li">
          use the Services for scams, fraud, spam, commercial solicitation, or pyramid schemes
        </li>
        <li className="legal-li">
          pressure others for contact information, meetings, sexual content, or off-platform
          interactions they do not want
        </li>
        <li className="legal-li">
          scrape, crawl, reverse engineer, or use bots, scripts, or automation to access or interact
          with the Services except as we expressly permit
        </li>
        <li className="legal-li">
          circumvent safety, moderation, matching, access, or rate-limiting features
        </li>
        <li className="legal-li">
          collect or use other users&apos; personal information for unauthorized purposes
        </li>
        <li className="legal-li">
          upload malware or interfere with the operation, security, or integrity of the Services
        </li>
        <li className="legal-li">violate any applicable law or third-party right</li>
      </ul>
      <p className="legal-p">
        We may investigate violations and take action including content removal, feature
        restrictions, suspension, or termination.
      </p>

      <h2 className="legal-h2">6. Child safety</h2>
      <p className="legal-p">
        Kynward strictly prohibits child sexual abuse and exploitation, including CSAM, grooming,
        sextortion, trafficking, or any sexual exploitation of minors. Users must not create,
        upload, share, request, or promote such content or behavior. Violations may result in
        immediate content removal, account termination, and reporting to appropriate authorities
        where required by law.
      </p>
      <p className="legal-p">
        Our published{' '}
        <Link className="legal-a" to="/child-safety">
          Child Safety Standards for Kynward
        </Link>{' '}
        describe how we enforce these rules and how to contact us about child safety.
      </p>

      <h2 className="legal-h2">7. Safety and interactions with others</h2>
      <p className="legal-p">
        You are solely responsible for your interactions with other users, both online and in
        person. Exercise caution and use your judgment when communicating with or meeting others.
        Read our{' '}
        <Link className="legal-a" to="/legal/safety-policy">
          Safety Policy
        </Link>{' '}
        for more information about reporting, background screening, and safer use of Kynward.
      </p>
      <p className="legal-p">
        Elyxora does not conduct criminal background checks on users unless we state otherwise in
        our Safety Policy. We do not guarantee the identity, intentions, or conduct of any user.
      </p>

      <h2 className="legal-h2">8. Subscriptions and payments</h2>
      <p className="legal-p">
        Some features require a paid subscription (for example, Kynward Plus). If you purchase a
        subscription through Apple, Google, or Stripe, the applicable store or payment provider
        processes your payment and may collect billing information according to its own terms and
        privacy policy.
      </p>
      <p className="legal-p">
        Subscriptions renew automatically at the end of each billing period unless canceled before
        renewal. The renewal term, price, and billing frequency are shown at purchase. You must
        manage or cancel subscriptions through the platform where you purchased them (Apple App
        Store, Google Play, or our web checkout, as applicable). Deleting your Kynward account does
        not automatically cancel a store subscription.
      </p>
      <p className="legal-p">
        Except where required by applicable law or the relevant app store policy, payments are
        non-refundable. We may change subscription prices or features with notice where required by
        law or store policy.
      </p>

      <h2 className="legal-h2">9. Intellectual property</h2>
      <p className="legal-p">
        The Services, including software, design, text, graphics, logos, trademarks, and other
        content provided by Elyxora (excluding User Content), are owned by Elyxora or its licensors
        and are protected by intellectual property laws. Except for the limited right to use the
        Services as permitted by these Terms, no rights are granted to you.
      </p>
      <p className="legal-p">
        You may not copy, modify, distribute, sell, lease, or create derivative works from the
        Services or Elyxora content except as expressly allowed by law or with our prior written
        consent.
      </p>

      <h2 className="legal-h2">10. Copyright complaints (DMCA)</h2>
      <p className="legal-p">
        If you believe content on the Services infringes your copyright, send a notice to our
        designated agent with:
      </p>
      <ul className="legal-ul">
        <li className="legal-li">your physical or electronic signature</li>
        <li className="legal-li">
          identification of the copyrighted work and the material you claim is infringing
        </li>
        <li className="legal-li">your contact information</li>
        <li className="legal-li">
          a statement that you have a good-faith belief the use is not authorized
        </li>
        <li className="legal-li">
          a statement, under penalty of perjury, that the information is accurate and that you are
          authorized to act on behalf of the copyright owner
        </li>
      </ul>
      <p className="legal-p">
        Send DMCA notices to: Elyxora Works, LLC — DMCA Agent, {MAILING_ADDRESS},{' '}
        <a className="legal-a" href={`mailto:${SUPPORT_EMAIL}`}>
          {SUPPORT_EMAIL}
        </a>
        . We may remove or disable access to allegedly infringing material and terminate repeat
        infringers where appropriate.
      </p>

      <h2 className="legal-h2">11. Moderation and enforcement</h2>
      <p className="legal-p">
        We may review, monitor, remove, restrict, or disable access to User Content or accounts to
        enforce these Terms, our Community Guidelines, applicable law, and the safety of users. We
        are not obligated to monitor all content and do not guarantee that objectionable content
        will be removed.
      </p>
      <p className="legal-p">
        We may suspend or terminate your account at any time, with or without notice, if we believe
        you have violated these Terms or created risk or legal exposure for Elyxora or others.
      </p>

      <h2 className="legal-h2">12. Disclaimers</h2>
      <p className="legal-p" style={{ fontWeight: 700 }}>
        THE SERVICES ARE PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE.&quot; TO THE FULLEST
        EXTENT PERMITTED BY LAW, ELYXORA DISCLAIMS ALL WARRANTIES, WHETHER EXPRESS, IMPLIED, OR
        STATUTORY, INCLUDING IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
        PURPOSE, TITLE, AND NON-INFRINGEMENT. ELYXORA DOES NOT WARRANT THAT THE SERVICES WILL BE
        UNINTERRUPTED, SECURE, ERROR-FREE, OR FREE OF HARMFUL CONTENT, OR THAT ANY MATCH, MESSAGE,
        MEETUP, OR INTERACTION WILL BE SAFE OR SUCCESSFUL.
      </p>
      <p className="legal-p">
        Some jurisdictions do not allow certain warranty exclusions, so some of the above may not
        apply to you.
      </p>

      <h2 className="legal-h2">13. Limitation of liability</h2>
      <p className="legal-p" style={{ fontWeight: 700 }}>
        TO THE FULLEST EXTENT PERMITTED BY LAW, ELYXORA AND ITS OFFICERS, DIRECTORS, EMPLOYEES,
        AGENTS, AFFILIATES, AND LICENSORS WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
        CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, DATA, GOODWILL, OR
        OTHER INTANGIBLE LOSSES, ARISING OUT OF OR RELATED TO YOUR USE OF OR INABILITY TO USE THE
        SERVICES OR YOUR INTERACTIONS WITH OTHER USERS, WHETHER BASED ON WARRANTY, CONTRACT, TORT
        (INCLUDING NEGLIGENCE), STATUTE, OR ANY OTHER LEGAL THEORY, EVEN IF ELYXORA HAS BEEN ADVISED
        OF THE POSSIBILITY OF SUCH DAMAGES.
      </p>
      <p className="legal-p" style={{ fontWeight: 700 }}>
        TO THE FULLEST EXTENT PERMITTED BY LAW, ELYXORA&apos;S TOTAL LIABILITY FOR ANY CLAIM ARISING
        OUT OF OR RELATING TO THESE TERMS OR THE SERVICES WILL NOT EXCEED THE GREATER OF (A) ONE
        HUNDRED U.S. DOLLARS (US $100) OR (B) THE AMOUNT YOU PAID ELYXORA FOR THE SERVICES IN THE
        TWELVE (12) MONTHS BEFORE THE EVENT GIVING RISE TO THE CLAIM.
      </p>
      <p className="legal-p">
        Some jurisdictions do not allow limitation of certain damages, so some of the above may not
        apply to you.
      </p>

      <h2 className="legal-h2">14. Indemnification</h2>
      <p className="legal-p">
        To the fullest extent permitted by law, you agree to defend, indemnify, and hold harmless
        Elyxora and its officers, directors, employees, agents, affiliates, and licensors from and
        against any claims, liabilities, damages, losses, and expenses (including reasonable
        attorneys&apos; fees) arising out of or related to: (a) your use of the Services; (b) your
        User Content; (c) your interactions with other users; or (d) your violation of these Terms,
        our policies, or applicable law.
      </p>

      <h2 className="legal-h2">15. Dispute resolution and arbitration</h2>
      <p className="legal-p" style={{ fontWeight: 700 }}>
        PLEASE READ THIS SECTION CAREFULLY. IT AFFECTS YOUR LEGAL RIGHTS, INCLUDING YOUR RIGHT TO
        FILE A LAWSUIT IN COURT AND TO HAVE A JURY TRIAL.
      </p>
      <p className="legal-p">
        <strong>Informal resolution.</strong> Before filing a claim, you agree to contact us at{' '}
        <a className="legal-a" href={`mailto:${SUPPORT_EMAIL}`}>
          {SUPPORT_EMAIL}
        </a>{' '}
        and attempt to resolve the dispute informally for at least thirty (30) days.
      </p>
      <p className="legal-p">
        <strong>Binding arbitration.</strong> Except for disputes that qualify for small claims
        court or seek injunctive relief for intellectual property misuse, any dispute arising out of
        or relating to these Terms or the Services will be resolved by binding individual
        arbitration administered by the American Arbitration Association under its Consumer
        Arbitration Rules. The arbitrator may award the same damages and relief that a court could
        award on an individual basis.
      </p>
      <p className="legal-p">
        <strong>Class action waiver.</strong> You and Elyxora agree that each may bring claims
        against the other only in an individual capacity, and not as a plaintiff or class member in
        any purported class, collective, representative, or mass action.
      </p>
      <p className="legal-p">
        <strong>Exceptions.</strong> Either party may bring an individual claim in small claims
        court if it qualifies. Nothing in this section prevents either party from seeking injunctive
        relief in court to stop unauthorized use or infringement of intellectual property. Claims of
        sexual assault or sexual harassment occurring in connection with your use of the Services
        are not subject to mandatory arbitration to the extent prohibited by applicable law.
      </p>

      <h2 className="legal-h2">16. Governing law and venue</h2>
      <p className="legal-p">
        These Terms are governed by the laws of the State of Texas, excluding its conflict-of-law
        rules. For disputes not subject to arbitration, you and Elyxora consent to the exclusive
        jurisdiction of the state and federal courts located in Travis County, Texas, and waive any
        objection to venue or inconvenient forum, except where prohibited by law.
      </p>

      <h2 className="legal-h2">17. Changes to these Terms</h2>
      <p className="legal-p">
        We may update these Terms from time to time. If we make material changes, we will update the
        &quot;Last updated&quot; date and provide additional notice where required by law. Your
        continued use of the Services after changes become effective constitutes acceptance of the
        updated Terms.
      </p>

      <h2 className="legal-h2">18. General</h2>
      <p className="legal-p">
        These Terms, together with the Privacy Policy, Community Guidelines, Safety Policy, and
        other policies referenced here, constitute the entire agreement between you and Elyxora
        regarding the Services. If any provision is found unenforceable, the remaining provisions
        remain in effect. Our failure to enforce a provision is not a waiver. You may not assign
        these Terms without our consent; we may assign them as part of a merger, acquisition, or
        sale of assets.
      </p>

      <h2 className="legal-h2">19. Contact</h2>
      <p className="legal-p">Questions about these Terms may be sent to:</p>
      <p className="legal-p" style={{ fontWeight: 600 }}>
        Elyxora Works, LLC
      </p>
      <div className="legal-contact-line">
        <span>Email: </span>
        <a className="legal-a" href={`mailto:${SUPPORT_EMAIL}`}>
          {SUPPORT_EMAIL}
        </a>
      </div>
      <p className="legal-p">Address: {MAILING_ADDRESS}</p>
    </LegalChrome>
  );
}
